import { Row, Col, Form, InputGroup, Button, Card } from 'react-bootstrap';
import AppLoader from 'components/general/AppLoader';
import { toast } from 'react-toastify';
import { useSelector } from "react-redux";
import { useState, useEffect, useCallback } from 'react';
import Select from 'components/general/ViewportSelect';

export default function AgendaItemAddNewModal({ meetingId, getMeeting, prevPos = null, initiallyOpen = false, onClose }) {
    // Track theme for live Select style updates
    const [theme, setTheme] = useState(document.body.getAttribute('data-theme') || 'light');

    useEffect(() => {
        // Listen for changes to body[data-theme]
        const observer = new MutationObserver(() => {
            setTheme(document.body.getAttribute('data-theme') || 'light');
        });
        observer.observe(document.body, { attributes: true, attributeFilter: ['data-theme'] });
        return () => observer.disconnect();
    }, []);
    const { user: authUser } = useSelector(x => x.auth);
    const url = process.env.REACT_APP_URL;

    // console.log(meetingId);
    const [title, setTitle] = useState('');
    const [role, setRole] = useState('');
    const [event, setEvent] = useState('');
    const [meetingRoles, setMeetingRoles] = useState([]);
    const [agendaEvents, setAgendaEvents] = useState([]);

    const [saveLoading, setSaveLoading] = useState(false);
    const [showForm, setShowForm] = useState(initiallyOpen);
    const [selectedEventType, setSelectedEventType] = useState('');

    const handleCloseForm = () => {
        setShowForm(false);
        setTitle('');
        setRole('');
        setEvent('');
        setSelectedEventType('');
        onClose?.();
    };

    const fetchMeetingRolesAndEvents = useCallback(() => {
        fetch(`${url}/meetingroles`, { method: 'GET' })
            .then(res => res.json())
            .then(data => setMeetingRoles(data))
            .catch(err => console.error(err));

        fetch(`${url}/agendaEvents`, { method: 'GET' })
            .then(res => res.json())
            .then(data => setAgendaEvents(data))
            .catch(err => console.error(err));
    }, [url]);

    useEffect(() => {
        if (initiallyOpen) fetchMeetingRolesAndEvents();
    }, [initiallyOpen, fetchMeetingRolesAndEvents]);


    const handleShowForm = () => {
        if (!showForm) fetchMeetingRolesAndEvents();
        setShowForm(true);
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        setSaveLoading(true);
        let position = '';
        if (!prevPos) {
            position = 1;
        } else {
            position = Number(prevPos) + 1;
        }
        const validate = true;
        if (validate === true) {
            fetch(url + '/agendaItem/' + meetingId, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': 'Bearer ' + authUser.token
                },
                body: JSON.stringify({
                    item_title: title,
                    meetingRole: role,
                    agendaEvent: event,
                    position: position
                })
            })
                .then(res => {
                    if (res.status !== 200 && res.status !== 201) {
                        throw new Error('(' + res.status + ') We could not save your new agenda Item');
                    }
                    return res.json();
                })
                .then(resData => {
                    setSaveLoading(false);
                    getMeeting();
                    handleCloseForm();
                })
                .catch(err => {
                    toast.error('' + err, { position: 'bottom-left' });
                    setSaveLoading(false);
                });
        }
    };

    // Theme-aware react-select styles using CSS variables
    const getSelectStyles = () => {
        const root = document.body;
        const computed = getComputedStyle(root);
        // Fallbacks for variables if not found
        const bg = computed.getPropertyValue('--tm-light-bg') || '#fff';
        const darkBg = computed.getPropertyValue('--tm-dark-bg') || '#23272b';
        const fg = computed.getPropertyValue('--foreground') || '#222';
        const border = computed.getPropertyValue('--modal-borders') || '#ccc';
        const placeholder = computed.getPropertyValue('--tm-gray-text') || '#666';
        // Use theme from state (updates on theme switch)
        const controlBg = theme === 'dark' ? darkBg : bg;
        const menuBg = theme === 'dark' ? darkBg : bg;
        const textColor = theme === 'dark' ? '#fff' : fg;
        const optionSelected = theme === 'dark' ? '#495057' : '#e9ecef';
        const optionFocused = theme === 'dark' ? '#495057' : '#f1f3f5';
        return {
            control: (provided) => ({
                ...provided,
                backgroundColor: controlBg,
                color: textColor,
                borderColor: border,
            }),
            menu: (provided) => ({
                ...provided,
                backgroundColor: menuBg,
                color: textColor,
            }),
            singleValue: (provided) => ({
                ...provided,
                color: textColor,
            }),
            option: (provided, state) => ({
                ...provided,
                backgroundColor: state.isSelected
                    ? optionSelected
                    : state.isFocused
                        ? optionFocused
                        : menuBg,
                color: textColor,
            }),
            input: (provided) => ({
                ...provided,
                color: textColor,
            }),
            placeholder: (provided) => ({
                ...provided,
                color: placeholder,
            }),
        };
    };

    const eventOptions = [...agendaEvents]
        .sort((a, b) => String(a?.name || '').localeCompare(String(b?.name || '')))
        .map(event => ({ value: event._id, label: event.name, eventType: event.eventType }));
    const roleOptions = [...meetingRoles]
        .sort((a, b) => String(a?.name || '').localeCompare(String(b?.name || '')))
        .map(role => ({ value: role._id, label: role.name }));

    return (
        <>
            <Row>
                <Col className='addAgendaItem'>
                    {!showForm ? (
                        <h2 onClick={handleShowForm} style={{ cursor: 'pointer' }}><i className='bi bi-plus-circle'></i></h2>
                    ) : (
                        <Card className='my-2 p-3'>
                            <Card.Header>Add New Agenda Item</Card.Header>
                            <Form onSubmit={handleSubmit}>
                                <Card.Body>
                                    <InputGroup className='mb-3'>
                                        <Form.Control
                                            className='formInput'
                                            placeholder='Title'
                                            aria-label='Title'
                                            name='title'
                                            value={title}
                                            onChange={e => setTitle(e.target.value)}
                                        />
                                    </InputGroup>
                                    <Select
                                        options={eventOptions}
                                        className='mb-3 formInput'
                                        name='event'
                                        value={eventOptions.find(option => option.value === event)}
                                        onChange={option => { setEvent(option ? option.value : ''); setSelectedEventType(option ? option.eventType : ''); }}
                                        isClearable
                                        isSearchable
                                        styles={getSelectStyles()}
                                        placeholder='Select Event'
                                    />
                                    {selectedEventType && (
                                        <div className='mb-3' style={{ fontSize: '0.8rem', fontStyle: 'italic', color: '#FFD700' }}>
                                            {selectedEventType}
                                        </div>
                                    )}
                                    <Select
                                        options={roleOptions}
                                        className='mb-3 formInput'
                                        name='role'
                                        value={roleOptions.find(option => option.value === role)}
                                        onChange={option => setRole(option ? option.value : '')}
                                        isClearable
                                        isSearchable
                                        styles={getSelectStyles()}
                                        placeholder='Select Role'
                                    />
                                </Card.Body>
                                <Card.Footer>
                                    {saveLoading ? (
                                        <>
                                            <AppLoader />
                                        </>
                                    ) : (
                                        <>
                                            <Button variant="success" type='submit'>
                                                <i className='bi bi-save'></i>
                                            </Button>{' '}
                                            <Button variant="danger" onClick={handleCloseForm} type='button'>
                                                <i className='bi bi-x-lg'></i>
                                            </Button>
                                        </>
                                    )}
                                </Card.Footer>
                            </Form>
                        </Card>
                    )}
                </Col>
            </Row>
        </>
    );
}
